import { useState } from 'react'

import { SquareDotLoadingIndicator } from '@joor/design-system'
import { useSelector } from 'react-redux'

import { OrderStatus } from '__generated__/atlas-types'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { isAccountTypeBrand } from 'store/currentUser/selectors'

import { convertGlobalIdToRetailerAccount } from '../../utils'
import {
  getBillingAddressDictionary,
  getPaymentsOrderData,
} from './Invoice.transformations'
import { useGetLocalCurrenciesFF } from './invoice.hooks'
import { PaymentOrderInfo, usePaymentOrderInfo } from './invoice.queries'
import {
  Container,
  ContentWrapper,
  LoaderContainer,
  Wrapper,
} from './invoice.ui'
import ManualTransactions from 'features/Orders/PublicInvoice/components/ManualTransactions'
import Payment from 'features/Orders/PublicInvoice/components/Payment'
import TransactionHistory from 'features/Orders/PublicInvoice/components/TransactionHistory'
import { getCurrencyByCountry } from 'features/Orders/PublicInvoice/components/helpers'
import SendPaymentRequestModal from 'features/Orders/Review/modals/SendPaymentRequestModal/SendPaymentRequestModal'
import { formatOrderAddress } from 'features/Orders/Review/utils/addresses.utils'
import { WarningBanner } from 'features/Orders/components/WarningBanner/WarningBanner'

const SendInvoiceButton = ({ order }: { order: PaymentOrderInfo }) => {
  const [isSendPaymentRequestModalOpen, setIsSendPaymentRequestModalOpen] =
    useState(false)

  return (
    <>
      {isSendPaymentRequestModalOpen && (
        <SendPaymentRequestModal
          isOpen={isSendPaymentRequestModalOpen}
          setIsOpen={setIsSendPaymentRequestModalOpen}
          orderId={order?.id}
          orderPO={order?.referenceNumber}
          orderCurrency={order?.priceType?.currency?.code}
          retailerId={convertGlobalIdToRetailerAccount(
            order?.retailerProfile?.id,
          )}
          retailerName={order?.retailerProfile?.displayName}
          retailerEmail={
            order?.shipping?.address?.email || order?.retailerProfile?.email
          }
          webOrderId={order?.webOrderLookupId}
          brandId={order?.brand?.id}
          brandName={order?.brand?.name}
          billingAddress={formatOrderAddress(order?.billing?.address)}
          hasRefetchPaymentInfo
        />
      )}
    </>
  )
}

export const Invoice = () => {
  const [forceRerenderDate, setForceRerenderDate] = useState(new Date())
  const { order, loading: orderQueryLoading } = usePaymentOrderInfo()
  const isBrandUser = useSelector(isAccountTypeBrand)

  const [canConvertCurrency, setCanConvertCurrency] = useState(false)

  const localCurrency = getCurrencyByCountry(order?.billing.address.country)

  const { data: localCurrenciesFF, loading: localCurrenciesFFLoading } =
    useGetLocalCurrenciesFF({
      brandId: order?.brand.id,
      skipWhen: localCurrency === order?.priceType?.currency?.code,
    })

  if (orderQueryLoading || !order || localCurrenciesFFLoading) {
    return (
      <LoaderContainer>
        <SquareDotLoadingIndicator className="loader" />
      </LoaderContainer>
    )
  }

  const paymentOrder = getPaymentsOrderData(order)
  const billingAddressDictionary = getBillingAddressDictionary(order)

  const retailerAccountId =
    toGlobalId('Account', fromGlobalId(order.retailerProfile!.id).id) ?? ''
  const accountId = toGlobalId('Account', fromGlobalId(order.brand.id).id) ?? ''

  const showCanceledBanner = order.status === OrderStatus.CANCELLED

  const handleCanCovertCurrency = (canConvert: boolean) => {
    setCanConvertCurrency(canConvert)
  }

  return (
    <Container>
      {isBrandUser && !showCanceledBanner && (
        <SendInvoiceButton order={order} />
      )}
      {showCanceledBanner && (
        <WarningBanner
          className="warning-banner"
          text="This order was cancelled"
          date={order.statusModified}
        />
      )}
      <ContentWrapper>
        <Wrapper>
          <TransactionHistory
            orderId={order.id}
            accountId={retailerAccountId}
            currencyCode={order.priceType?.currency?.code}
            shouldUseLocalCurrencies={localCurrenciesFF && canConvertCurrency}
            billingCountry={billingAddressDictionary.country}
          />
          <ManualTransactions
            orderInfo={paymentOrder}
            accountId={accountId}
            isBrand={isBrandUser}
          />
        </Wrapper>
        <Payment
          order={paymentOrder}
          retailerId={order.retailerProfile.id}
          brandId={order.brand.id}
          key={`${forceRerenderDate}`}
          loading={orderQueryLoading}
          shouldUseLocalCurrencies={localCurrenciesFF}
          setForceRerenderDate={setForceRerenderDate}
          billingAddressDictionary={billingAddressDictionary}
          disablePayment={order.status === OrderStatus.CANCELLED}
          onRatesFetched={handleCanCovertCurrency}
        />
      </ContentWrapper>
    </Container>
  )
}
